import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { hideModal, showModal } from '../../store/actions/modalActions';
import CollaboratorSelector, { Collaborator } from '../shared/CollaboratorSelector';
import Button from '../shared/TailwindComponents/Button';
import { manageAccess } from '../../utils/FileApiBuilder';
import { RootState, useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

interface ManageAccessModalProps {
  itemId: string;
  itemType: 'file' | 'folder';
  itemName: string;
  itemCollaborators: any[];
  currentUserId?: string;
  creatorId?: string;
}

interface CollaboratorWithAdmin extends Collaborator {
  is_admin?: boolean;
}

const ManageAccessModal: React.FC<ManageAccessModalProps> = ({
  itemId,
  itemType,
  itemName,
  itemCollaborators,
  currentUserId,
  creatorId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const [collaborators, setCollaborators] = useState<CollaboratorWithAdmin[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize collaborators from props
  useEffect(() => {
    if (itemCollaborators && itemCollaborators.length >= 0) {
      // Mark the creator/admin user - they cannot be removed or have access changed
      const markedCollaborators = itemCollaborators.map(collab => ({
        ...collab,
        is_admin: collab.user_id === creatorId || collab.user_uuid === creatorId || collab.collaborator_uuid === creatorId,
      }));
      setCollaborators(markedCollaborators);
    }
  }, [itemCollaborators, creatorId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Call manage access API using FileApiBuilder for consistency
      await manageAccess({
        item_id: itemId,
        item_type: itemType,
        collaborators: collaborators.map(c => ({
          user_id: c.collaborator_uuid || String(c.user_id), // Send UUID as user_id
          access: c.access,
        })),
      });

      // Close modal
      dispatch(hideModal());

      // Force Files.tsx to re-fetch data by updating the URL search params
      // The main useEffect in Files.tsx watches location.search, so this triggers a refresh
      const refreshParams = new URLSearchParams(location.search);
      refreshParams.set('_r', Date.now().toString());
      history.replace(`${location.pathname}?${refreshParams.toString()}`);

      // Show success message
      // dispatch(
      //   showFeedbackModal({
      //     modalType: 'success',
      //     message: t('Access updated successfully'),
      //     showModal: true,
      //     duration: 3000,
      //   })
      // );
    } catch (error) {
      console.error('Error updating access:', error);
      dispatch(
        showFeedbackModal({
          modalType: 'error',
          message: t('Error updating access'),
          showModal: true,
          duration: 3000,
        })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    dispatch(hideModal());
  };

  // Show loading state while people are being loaded in CollaboratorSelector
  // No need for separate state since CollaboratorSelector handles it
  
  return (
    <div className="min-w-[500px] sm:w-full">
        <div className='px-4'>
      <CollaboratorSelector
        collaborators={collaborators}
        onCollaboratorsChange={setCollaborators}
        allowedPermissions={['edit', 'view']}
        adminUuid={creatorId}
      />
      </div>

      <div className="mt-6 pt-6 border-t border-1 border-gray-300 dark:border-gray-700 flex justify-end gap-2 px-4">
        <Button
          variant="primary"
          size="small"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? t('Saving...') : t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default ManageAccessModal;
